const supportsAdoptedStyleSheets = (): boolean => {
  try {
    return (
      "adoptedStyleSheets" in Document.prototype &&
      typeof CSSStyleSheet !== "undefined" &&
      "replaceSync" in CSSStyleSheet.prototype
    );
  } catch {
    return false;
  }
};

const injectStyleTag = (shadow: ShadowRoot, css: string): void => {
  const tag = document.createElement("style");
  tag.textContent = css;
  shadow.appendChild(tag);
};

/**
 * Add CSS to an existing shadow root. Appends rather than replaces, so it can
 * layer on top of styles that arrived via Declarative Shadow DOM. Prefers
 * `adoptedStyleSheets` (CSP-friendly) with a `<style>`-tag fallback.
 */
export const injectStyles = (shadow: ShadowRoot, css: string): void => {
  if (supportsAdoptedStyleSheets()) {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, sheet];
      return;
    } catch {
      /* fall through to <style> tag */
    }
  }
  injectStyleTag(shadow, css);
};

/**
 * Attach a shadow root to `host` and inject the given CSS.
 */
export const setupShadow = (host: Element, css: string): ShadowRoot => {
  const shadow = host.attachShadow({ mode: "open" });
  injectStyles(shadow, css);
  return shadow;
};
